/**
 * Resolve patient display names for consumption list/detail rows.
 * Consumption APIs typically return only patient_id; names come from
 * allocated IPD admissions, then get_filtered_patient_data as fallback.
 */
import {
  getAllocatedAdmissions,
  searchPatients,
} from '../../ipd/pages/patient-admission-ipd/api/registrationService';

function pickName(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

export function consumptionPatientId(row) {
  return String(
    row?.patient_id
    || row?.patientId
    || row?.patient?.patient_id
    || '',
  ).trim();
}

export function patientNameFromConsumption(row) {
  const nested = row?.patient || {};
  return pickName(
    row?.patient_name,
    row?.patientName,
    nested.name,
    nested.patient_name,
    [nested.first_name, nested.last_name].filter(Boolean).join(' '),
  );
}

function nameFromAdmission(row) {
  const patient = row?.patient || {};
  return pickName(
    patient.name,
    patient.patient_name,
    [patient.first_name, patient.last_name].filter(Boolean).join(' '),
  );
}

export function admissionsToNameMap(admissions) {
  const map = {};
  (admissions || []).forEach((row) => {
    const id = String(row?.patient?.patient_id || row?.patient_id || '').trim();
    const name = nameFromAdmission(row);
    if (id && name) map[id] = name;
  });
  return map;
}

export function formatConsumptionPatient(row, nameById = {}) {
  const id = consumptionPatientId(row);
  if (!id) return '—';
  const name = patientNameFromConsumption(row) || nameById[id] || '';
  return name ? `${name} (${id})` : id;
}

export async function resolveConsumptionPatientNames(rows) {
  const ids = [...new Set((rows || []).map(consumptionPatientId).filter(Boolean))];
  const map = {};
  (rows || []).forEach((row) => {
    const id = consumptionPatientId(row);
    const name = patientNameFromConsumption(row);
    if (id && name) map[id] = name;
  });

  const missingAfterRow = ids.filter(id => !map[id]);
  if (!missingAfterRow.length) return map;

  try {
    const admissions = await getAllocatedAdmissions();
    Object.assign(map, admissionsToNameMap(admissions));
  } catch (e) {
    /* keep unresolved ids */
  }

  const stillMissing = ids.filter(id => !map[id]);
  if (!stillMissing.length) return map;

  await Promise.all(stillMissing.map(async (id) => {
    try {
      const found = await searchPatients({ searchType: 'PatID', searchQuery: id });
      const first = Array.isArray(found) ? found[0] : found;
      const name = pickName(first?.name, first?.patient_name);
      if (name) map[id] = name;
    } catch (e) {
      /* leave as id */
    }
  }));

  return map;
}
